import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { RequirementStatus } from '@/bundles/Requirements/types/statusTypes';
import { Nullable } from '@/utils/types';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { getPropertyTypesForFilters } from '@/bundles/GroupFilters/consts';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getRequirementFilters = (): IFilterConfigItem[] => {
  return [
    // FilterCategoryEnum.requirement_details start
    {
      title: 'Use Type',
      name: 'requirement_use_types',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Transaction Type',
      name: 'requirement_transaction_type',
      items: [
        { text: 'Purchase', value: 'purchase' },
        { text: 'Lease', value: 'lease' },
      ],
      type: FilterTypeEnum.one_select,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Company',
      name: 'requirement_company',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.presentation_details,
    },
    {
      title: 'Building Size Min',
      name: 'requirement_building_size_min',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.requirement_details,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Building Size Max',
      name: 'requirement_building_size_max',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.requirement_details,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Land Size Min',
      name: 'requirement_land_size_min', // sending with requirement_land_size_min_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Land Size Max',
      name: 'requirement_land_size_max', // sending with requirement_land_size_max_unit
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Transaction Min',
      name: 'requirement_transaction_size_min',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Transaction Max',
      name: 'requirement_transaction_size_max',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Office Min',
      name: 'requirement_office_size_min',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.requirement_details,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Office Max',
      name: 'requirement_office_size_max',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.requirement_details,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Property Type',
      name: 'requirement_property_type',
      items: getPropertyTypesForFilters(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Requirement Status',
      name: 'requirement_status',
      items: [
        { text: 'Active', value: RequirementStatus.active },
        { text: 'On Hold', value: RequirementStatus.on_hold },
        { text: 'Purchase Completed', value: RequirementStatus.purchase_completed },
        { text: 'Lease Completed', value: RequirementStatus.lease_completed },
        { text: 'Dead', value: RequirementStatus.dead },
      ],
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Target Occupancy Date',
      name: 'requirement_target_occupancy_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Brokers',
      name: 'requirement_brokers',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Broker Company',
      name: 'requirement_broker_company',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.requirement_details,
    },
    {
      title: 'Permissioned for',
      name: 'requirement_permission_view',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.requirement_details,
    },
    // FilterCategoryEnum.requirement_details end
    // FilterCategoryEnum.contact_filters start
    {
      title: 'Contact Name',
      name: 'requirement_contact_name',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.contact_filters,
    },
    {
      title: 'Contact Requirement Role',
      name: 'requirement_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getContactRolesBySource('requirement'),
      category: FilterCategoryEnum.contact_filters,
    },
    // FilterCategoryEnum.contact_filters end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'requirement_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'requirement_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'requirement_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'requirement_view',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'requirement_created_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'requirement_edited_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('requirement'),
  ].filter(Boolean) as IFilterConfigItem[];
};
